import { useEffect, useRef } from 'react';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { specValue } from '../../domain/cars/carFigures';
import { formatSpecNumber, formatYearRange } from '../../domain/cars/carProperties';
import { HERO_TAKE, heroTake } from '../../domain/cars/carSchema';
import { artSource } from '../cars/carArt';
import { CheckIcon, CloseIcon } from '../icons/ControlIcons';

/**
 * Choosing a car for a lane (SPEC-008 PR-8.8).
 *
 * This was a native `<select>` and the argument for that was a real one:
 * a dozen-odd options is the size at which the platform's own wheel beats anything
 * an app would draw, and it is keyboard- and screen-reader-complete for nothing.
 * What it cannot do is show a CAR. A list of two dozen names in the system font
 * is the one surface in this product where the artwork — the whole proposition —
 * is replaced by text, and it is the surface a reader touches first.
 *
 * So the platform's picker is gone and this is drawn instead, on the one
 * condition that made that acceptable: it is a real `<dialog>` opened with
 * `showModal()`, exactly as `GarageFilterSheet` is. Focus trapping, Escape, the
 * top layer and an inert background are four things the platform gets right and
 * a hand-rolled sheet gets subtly wrong — so what is hand-drawn here is the
 * PICTURE and never the modal behaviour.
 *
 * Each row is the car as the strip will draw it: the mirrored side profile, its
 * name, the years it was built and its power-to-weight, which is the figure this
 * whole screen is arranged around. A reader choosing an opponent is choosing on
 * the number they are about to watch decide the race.
 *
 * The options are plain `<button>`s rather than a `listbox` of `option`s. A
 * listbox promises arrow-key roving that would have to be written and got
 * exactly right; a list of buttons already tabs, already activates on Enter and
 * Space, and already reads correctly — and choosing here is an ACTION that
 * closes the sheet rather than a selection sitting in a group waiting to be
 * confirmed.
 */

/** The row for the car already in this lane, and the one in the other. */
function optionState(car, chosenId, rivalId) {
    if (car.id === chosenId) {
        return 'chosen';
    }

    return car.id === rivalId ? 'rival' : null;
}

function CarOption({ car, onChoose, state }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const art = artSource(car, heroTake(car));
    const powerToWeight = specValue(car, 'powerToWeight');

    return (
        <li>
            <button
                type="button"
                className={`car-option${state ? ` car-option--${state}` : ''}`}
                onClick={() => onChoose(car.id)}
                // `aria-current` rather than `aria-pressed`: this is not a
                // toggle, it is the one row in the list that names where the
                // reader already is.
                aria-current={state === 'chosen' ? 'true' : undefined}
            >
                <span className="car-option__art">
                    {art ? (
                        <img
                            className={`car-option__image${art.take === HERO_TAKE ? ' car-option__image--profile' : ''}`}
                            src={art.src}
                            srcSet={art.srcSet}
                            sizes="120px"
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    ) : null}
                </span>

                <span className="car-option__identity">
                    <span className="car-option__name">{carName(car)}</span>
                    <span className="car-option__years">
                        {formatYearRange(car.yearsProduced, { languageCode })}
                        {/* Said in words, because the mark beside it is a tint
                            and a tint is not available to every reader. */}
                        {state === 'rival' ? ` · ${t('compare.select.inOtherLane')}` : ''}
                    </span>
                </span>

                <span className="car-option__figure">
                    <span className="car-option__value">
                        {formatSpecNumber(powerToWeight, { languageCode })}
                    </span>
                    <span className="car-option__unit">{t('cars.spec.powerToWeight.unit')}</span>
                </span>

                {/* Always in the document so choosing a row does not change its
                    width and shuffle the column — the same reason `FacetPill`
                    keeps its tick. */}
                <CheckIcon className="car-option__tick" size={16} />
            </button>
        </li>
    );
}

export default function CarSelectSheet({
    catalogue,
    chosenId,
    onChoose,
    onClose,
    rivalId,
    side,
}) {
    const t = useTranslate();
    const dialogRef = useRef(null);

    // Opening is a one-off, and deliberately its own effect: an effect that
    // depended on `onClose` would close and re-open the dialog whenever the
    // parent re-rendered, and the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    // One exit for every way out: the button closes it, and so do Escape and a
    // tap on the backdrop, which the browser turns into this same event.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    return (
        <dialog
            className="car-select"
            ref={dialogRef}
            aria-labelledby="car-select-title"
            // A click that lands on the dialog element ITSELF landed on the
            // backdrop: everything inside is a child of the panel.
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            <header className="car-select__head">
                <h2 className="car-select__title" id="car-select-title">
                    {t(side === 0 ? 'compare.select.titleA' : 'compare.select.titleB')}
                </h2>
                <button
                    type="button"
                    className="round-button"
                    onClick={() => dialogRef.current.close()}
                    aria-label={t('compare.select.close')}
                >
                    <CloseIcon size={20} />
                </button>
            </header>

            <ul className="car-select__list">
                {catalogue.map((car) => (
                    <CarOption
                        key={car.id}
                        car={car}
                        state={optionState(car, chosenId, rivalId)}
                        onChoose={onChoose}
                    />
                ))}
            </ul>
        </dialog>
    );
}
